import { action, computed, makeObservable, observable, toJS } from "mobx";
import { Editor, Range, Transforms } from "slate";
import { Mention } from "./slate-types";
import { KeyboardEvent } from "react";
import { ReactEditor } from "slate-react";

interface SuggestionItem {
  value: string;
  label: string;
}
export type Suggestions = SuggestionItem[];

export class MentionStore {
  editor: Editor = null;
  target: Range = null;
  index = 0;
  search = "";
  suggestions: Suggestions = [];
  private suggestionsMap: Map<string, string> = new Map();

  constructor(editor: Editor, suggestions: Suggestions = []) {
    this.editor = editor;
    this.suggestions = suggestions;
    this.suggestionsMap = new Map(suggestions.map((item) => [item.value, item.label]));
    makeObservable(this, {
      suggestions: observable,
      isMenuVisible: computed,
      mentions: computed,
      index: observable,
      search: observable,
      target: observable,
      // actions
      onEditorChange: action.bound,
      setIndex: action.bound,
      setTarget: action.bound,
      onKeyDown: action.bound,
      setSuggestions: action.bound,
    });
  }

  setSuggestions(suggestions: Suggestions) {
    this.suggestions = suggestions.slice();
    this.suggestionsMap = new Map(suggestions.map((item) => [item.value, item.label]));
  }

  getSuggestionLabel(value: string) {
    return this.suggestionsMap.get(value);
  }

  onEditorChange() {
    if (!this.suggestions.length) {
      return;
    }
    const { selection } = this.editor;
    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);

      // Get the text before cursor by going back character by character
      // We'll look back up to 50 characters to find an @
      let beforeRange = null;
      let beforeText = "";
      let charsBefore = 0;
      const maxLookBack = 50;

      // Build up the text before the cursor
      let currentPoint = start;
      while (charsBefore < maxLookBack) {
        const before = Editor.before(this.editor, currentPoint, { unit: "character" });
        if (!before) break;

        const range = Editor.range(this.editor, before, start);
        const text = Editor.string(this.editor, range);

        // Check if we have a match
        const match = text.match(/@(\w*)$/);
        if (match) {
          beforeRange = range;
          beforeText = text;
          break;
        }

        // If we hit a space or newline after finding some text, stop
        if (text.length > 0 && /[\s\n]/.test(text[0])) {
          break;
        }

        currentPoint = before;
        charsBefore++;
      }

      // Check if we found a valid @ mention pattern
      if (beforeRange && beforeText) {
        const beforeMatch = beforeText.match(/@(\w*)$/);
        const after = Editor.after(this.editor, start);
        const afterRange = after && Editor.range(this.editor, start, after);
        const afterText = afterRange ? Editor.string(this.editor, afterRange) : "";
        const afterMatch = afterText.match(/^(\s|$)/);

        if (beforeMatch && afterMatch) {
          this.target = beforeRange;
          this.search = beforeMatch[1];
          this.index = 0;
          return;
        }
      }
    }
    this.target = null;
  }

  setIndex(index: number) {
    this.index = index;
  }

  setTarget(range: Range) {
    this.target = range;
  }

  onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (this.target && this.suggestions.length > 0) {
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          const prevIndex = this.index >= this.mentions.length - 1 ? 0 : this.index + 1;
          this.setIndex(prevIndex);
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          const nextIndex = this.index <= 0 ? this.mentions.length - 1 : this.index - 1;
          this.setIndex(nextIndex);
          break;
        }
        case "Tab":
        case "Enter":
          event.preventDefault();
          Transforms.select(this.editor, toJS(this.target));
          this.insertMention(this.editor, this.mentions[this.index]);
          this.setTarget(null);
          break;
        case "Escape":
          event.preventDefault();
          this.setTarget(null);
          break;
      }
    }
  }

  insertMention(editor: Editor, item: SuggestionItem) {
    const mention: Mention = {
      type: "mention",
      id: item.value,
      label: item.label,
      children: [{ text: "" }],
    };
    Transforms.insertNodes(editor, mention);
    Transforms.move(editor);
  }

  selectMention(item: SuggestionItem) {
    Transforms.select(this.editor, toJS(this.target));
    this.insertMention(this.editor, item);
    this.setTarget(null);
    ReactEditor.focus(this.editor);
  }

  get mentions() {
    const query = this.search.toLowerCase();
    return this.suggestions.filter(
      ({ value, label }) => value.toLowerCase().indexOf(query) !== -1 || label.toLowerCase().indexOf(query) !== -1
    );
  }

  get isMenuVisible() {
    return !!this.mentions.length;
  }
}
